// File: src/pages/EditTransporter.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
  Input,
  Label,
  Button,
  Spinner,
} from "reactstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate, useParams } from "react-router-dom";
import swal from "sweetalert";

/* APIs */
import { _Get, _GetByUrlOnly, _Put } from "../import/ApiEndPoint/ApiCalling";
import {
  viewpincodedata,
  View_TransporterByID,
  Update_TransporterByID,
} from "../import/ApiEndPoint/Api";

/* ================= Opening Financial Year Helpers ================= */

const normalizeOpeningFinancialYear = (value) => {
  const raw = String(value || "").trim();

  const match = raw.match(/^(\d{4})\s*-\s*(\d{2}|\d{4})$/);
  if (!match) return raw;

  const startYear = match[1];
  const endYear = match[2].length === 4 ? match[2].slice(-2) : match[2];

  return `${startYear}-${endYear}`;
};

const getFYStartYear = (date = new Date()) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = d.getMonth();

  return m >= 3 ? y : y - 1;
};

const getCurrentOpeningFinancialYear = () => {
  const start = getFYStartYear(new Date());
  return normalizeOpeningFinancialYear(`${start}-${start + 1}`);
};

const buildOpeningFinancialYearOptions = (past = 5, future = 2) => {
  const currentStart = getFYStartYear(new Date());
  const arr = [];

  for (let y = currentStart - past; y <= currentStart + future; y++) {
    arr.push(normalizeOpeningFinancialYear(`${y}-${y + 1}`));
  }

  return arr.reverse();
};

const EditTransporter = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [Loading, setLoading] = useState(false);
  const [pinList, setPinList] = useState([]);

  const openingFYOptions = useMemo(
    () => buildOpeningFinancialYearOptions(5, 2),
    [],
  );

  const userData = JSON.parse(localStorage.getItem("userData") || "{}");

  const [form, setForm] = useState({
    companyName: "",
    id: "",
    name: "",
    contactNumber: "",
    email: "",
    pincode: "",
    City: "",
    State: "",
    address1: "",
    OpeningBalance: "",
    openingFinancialYear: getCurrentOpeningFinancialYear(),
    Type: "",
    status: "Active",
    gstNumber: "",
    database: userData?.database || "",
  });

  const [serviceArea, setServiceArea] = useState([
    {
      BMName: "",
      contactNumber: "",
      address: "",
      pincode: "",
      city: "",
      state: "",
    },
  ]);

  // inline CSS
  useEffect(() => {
    const sid = "transporter-inline-css";
    if (document.getElementById(sid)) return;

    const s = document.createElement("style");
    s.id = sid;
    s.textContent = `
      .sa { --fs:14px; --fs-lg:15px; --h:40px; --gap:8px; }
      .sa *{ font-size:var(--fs) !important; }
      .sa h1{ font-size:24px !important; font-weight:600; }
      .sa label{ font-size:13px !important; font-weight:600; margin-bottom:6px; }
      .sa input:not([type="radio"]):not([type="checkbox"]),
      .sa select,
      .sa textarea{ font-size:var(--fs-lg) !important; height:var(--h);
        padding:8px 10px; border-radius:10px; border:1px solid #d6d8e1; background:#fff; }
      .sa .btn{ border-radius:10px; padding:8px 14px; font-weight:500; }
      .sa .card-soft{ background:#f3f6ff; border:1px solid #e5e7ef; border-radius:12px; padding:12px; box-shadow: rgba(16, 24, 40, .04) 0 1px 2px; }
      .sa .row.g-tight{ row-gap: var(--gap); }
      .sa .myphoneinput{ height:var(--h) !important; font-size:var(--fs-lg) !important; padding:8px 10px !important; }
      .sa .radio-line{ display:flex; align-items:center; gap:18px; font-weight:600; }
      .sa .radio-line label{ display:flex; align-items:center; gap:8px; margin:0; }
      .sa .tag{ display:inline-block; background:#eef2ff; border:1px solid #e5e7eb; padding:2px 8px; border-radius:999px; font-size:12px; margin-left:6px; }
      .sa .block{ border:1px solid #e5e7eb; border-radius:12px; padding:12px; background:#fff; }
    `;
    document.head.appendChild(s);
  }, []);

  // pincode + transporter
  useEffect(() => {
    (async () => {
      try {
        const resPins = await _GetByUrlOnly(viewpincodedata);
        setPinList(resPins?.pinCodeList || []);
      } catch {}

      try {
        const res = await _Get(View_TransporterByID, id);
        const t = res?.Transporter || {};

        const normalizeStatus = (s) => {
          const v = String(s || "Active").toLowerCase();
          return v === "active" ? "Active" : "Deactive";
        };

        setForm((p) => ({
          ...p,
          companyName: t.companyName || "",
          id:
            t.id ||
            (t.companyName ? String(t.companyName).replace(/\s+/g, "") : ""),
          name: t.name || "",
          contactNumber: t.contactNumber || "",
          email: t.email || "",
          pincode: t.pincode || "",
          City: t.city || t.City || "",
          State: t.state || t.State || "",
          address1: t.address1 || t.address || "",
          OpeningBalance: t.OpeningBalance || "",
          openingFinancialYear: normalizeOpeningFinancialYear(
            t?.openingFinancialYear || getCurrentOpeningFinancialYear(),
          ),
          Type: t.Type || "",
          status: normalizeStatus(t.status),
          gstNumber: t.gstNumber || "",
          database: t.database || p.database,
        }));

        setServiceArea(
          Array.isArray(t?.serviceArea) && t.serviceArea.length
            ? t.serviceArea
            : [
                {
                  BMName: "",
                  contactNumber: "",
                  address: "",
                  pincode: "",
                  city: "",
                  state: "",
                },
              ],
        );
      } catch {
        swal("Error", "Unable to load transporter", "error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const lookup = (pin) => {
    const hit = (pinList || []).find(
      (x) => String(x.pincode || x.Pincode) === String(pin),
    );

    return {
      city: hit?.city || hit?.District || "",
      state: hit?.state || hit?.StateName || "",
    };
  };

  const setField = (name, value) => {
    if (name === "companyName") {
      return setForm((p) => ({
        ...p,
        companyName: value,
        id: value ? String(value).replace(/\s+/g, "") : p.id,
      }));
    }

    if (name === "openingFinancialYear") {
      return setForm((p) => ({
        ...p,
        openingFinancialYear: normalizeOpeningFinancialYear(value),
      }));
    }

    if (name === "pincode") {
      const { city, state } = lookup(value);

      return setForm((p) => ({
        ...p,
        pincode: value,
        City: city,
        State: state,
      }));
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSAChange = (i, key, val) =>
    setServiceArea((prev) => {
      const n = [...prev];
      n[i] = { ...n[i], [key]: val };
      return n;
    });

  const onSAPin = (i, val) => {
    const { city, state } = lookup(val);

    onSAChange(i, "pincode", val);
    onSAChange(i, "city", city);
    onSAChange(i, "state", state);
  };

  const addService = () =>
    setServiceArea((p) => [
      ...p,
      {
        BMName: "",
        contactNumber: "",
        address: "",
        pincode: "",
        city: "",
        state: "",
      },
    ]);

  const removeService = (i) =>
    setServiceArea((p) => p.filter((_, idx) => idx !== i));

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!form.companyName?.trim()) {
      return swal("Error", "Transporter name is required", "error");
    }

    setLoading(true);

    try {
      const fd = new FormData();

      fd.append("companyName", form.companyName);
      fd.append("database", form.database || "");
      fd.append("id", form.id || form.companyName.replace(/\s+/g, ""));

      fd.append("city", form.City || "");
      fd.append("state", form.State || "");

      const fields = [
        "name",
        "contactNumber",
        "email",
        "pincode",
        "City",
        "State",
        "address1",
        "OpeningBalance",
        "openingFinancialYear",
        "Type",
        "status",
        "gstNumber",
      ];

      fields.forEach((k) => {
        const v = form[k];

        if (v !== undefined && v !== null) {
          fd.append(k, v);
        }
      });

      fd.set(
        "openingFinancialYear",
        normalizeOpeningFinancialYear(
          form.openingFinancialYear || getCurrentOpeningFinancialYear(),
        ),
      );

      fd.append("serviceArea", JSON.stringify(serviceArea));

      const res = await _Put(Update_TransporterByID, id, fd);

      if (res?.status) {
        swal("Success", "Transporter Updated Successfully", "success");
        navigate("/app/ajgroup/CreateTransportList");
      } else {
        swal("Error", res?.message || "Update failed", "error");
      }
    } catch (err) {
      swal(
        "Error",
        err?.response?.data?.message || "Something went wrong",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa">
      <Card className="card-soft" style={{ padding: 12 }}>
        <Row className="align-items-center mb-2">
          <Col />

          <Col className="text-center">
            <h1 className="m-0">Edit Transporter</h1>
          </Col>

          <Col className="d-flex justify-content-end" lg="4" md="5" sm="12">
            <Button color="secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        <Form onSubmit={submitHandler}>
          <Row className="g-tight">
            {/* LEFT: Basic info */}
            <Col lg="6" md="12">
              <CardBody className="card-soft">
                <h1 className="text-center mb-1">Basic Information</h1>

                <Row className="g-tight">
                  <Col lg="6" md="6" sm="12">
                    <Label>Transporter Name *</Label>
                    <Input
                      value={form.companyName}
                      onChange={(e) => setField("companyName", e.target.value)}
                    />
                  </Col>

                  <Col lg="6" md="6" sm="12">
                    <Label>Incharge Name *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                    />
                  </Col>

                  <Col lg="6" md="6" sm="12">
                    <Label>Contact Number *</Label>
                    <PhoneInput
                      inputClass="myphoneinput"
                      country="in"
                      countryCodeEditable={false}
                      value={form.contactNumber}
                      onChange={(v) => setField("contactNumber", v)}
                    />
                  </Col>

                  <Col lg="6" md="6" sm="12">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                    />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>Pincode *</Label>
                    <Input
                      type="number"
                      value={form.pincode}
                      onChange={(e) => setField("pincode", e.target.value)}
                      onKeyDown={(e) =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault()
                      }
                    />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>City</Label>
                    <Input readOnly value={form.City} />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>State</Label>
                    <Input readOnly value={form.State} />
                  </Col>

                  <Col lg="12" md="12" sm="12">
                    <Label>Address</Label>
                    <Input
                      value={form.address1}
                      onChange={(e) => setField("address1", e.target.value)}
                    />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>Opening Financial Year *</Label>
                    <Input
                      type="select"
                      value={
                        form.openingFinancialYear ||
                        getCurrentOpeningFinancialYear()
                      }
                      onChange={(e) =>
                        setField(
                          "openingFinancialYear",
                          normalizeOpeningFinancialYear(e.target.value),
                        )
                      }
                    >
                      {openingFYOptions.map((fy) => (
                        <option key={fy} value={fy}>
                          {fy}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>Opening Balance *</Label>
                    <Input
                      type="number"
                      value={form.OpeningBalance}
                      onChange={(e) =>
                        setField("OpeningBalance", e.target.value)
                      }
                      onKeyDown={(e) =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault()
                      }
                    />
                  </Col>

                  <Col lg="4" md="6" sm="12">
                    <Label>O/P Balance Type *</Label>
                    <Input
                      type="select"
                      value={form.Type}
                      onChange={(e) => setField("Type", e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      <option value="debit">Dr</option>
                      <option value="credit">Cr</option>
                    </Input>
                  </Col>

                  <Col lg="4" md="12" sm="12">
                    <Label>Status</Label>
                    <div className="radio-line">
                      <label>
                        <Input
                          type="radio"
                          name="status"
                          checked={
                            String(form.status).toLowerCase() === "active"
                          }
                          onChange={() => setField("status", "Active")}
                        />
                        <span>Active</span>
                      </label>

                      <label>
                        <Input
                          type="radio"
                          name="status"
                          checked={
                            String(form.status).toLowerCase() === "deactive"
                          }
                          onChange={() => setField("status", "Deactive")}
                        />
                        <span>Deactive</span>
                      </label>
                    </div>
                  </Col>

                  <Col lg="6" md="6" sm="12">
                    <Label>GST Number (optional)</Label>
                    <Input
                      value={form.gstNumber}
                      onChange={(e) =>
                        setField(
                          "gstNumber",
                          e.target.value.toUpperCase().replace(/\s/g, ""),
                        )
                      }
                    />
                  </Col>
                </Row>
              </CardBody>
            </Col>

            {/* RIGHT: Service Locations */}
            <Col lg="6" md="12">
              <CardBody className="card-soft">
                <h1 className="text-center mb-1">
                  Service Locations{" "}
                  <span className="tag">{serviceArea.length}</span>
                </h1>

                {serviceArea.map((s, idx) => (
                  <div key={idx} className="block" style={{ marginBottom: 10 }}>
                    <Row className="g-tight">
                      <Col lg="6" md="6" sm="12">
                        <Label>Branch Manager</Label>
                        <Input
                          value={s.BMName}
                          onChange={(e) =>
                            onSAChange(idx, "BMName", e.target.value)
                          }
                        />
                      </Col>

                      <Col lg="6" md="6" sm="12">
                        <Label>Contact Number</Label>
                        <Input
                          value={s.contactNumber}
                          onChange={(e) =>
                            onSAChange(idx, "contactNumber", e.target.value)
                          }
                        />
                      </Col>

                      <Col lg="4" md="4" sm="12">
                        <Label>Pincode</Label>
                        <Input
                          type="number"
                          value={s.pincode}
                          onChange={(e) => onSAPin(idx, e.target.value)}
                          onKeyDown={(e) =>
                            ["e", "E", "+", "-"].includes(e.key) &&
                            e.preventDefault()
                          }
                        />
                      </Col>

                      <Col lg="4" md="4" sm="12">
                        <Label>City</Label>
                        <Input readOnly value={s.city} />
                      </Col>

                      <Col lg="4" md="4" sm="12">
                        <Label>State</Label>
                        <Input readOnly value={s.state} />
                      </Col>

                      <Col lg="12" md="12" sm="12">
                        <Label>Address</Label>
                        <Input
                          value={s.address}
                          onChange={(e) =>
                            onSAChange(idx, "address", e.target.value)
                          }
                        />
                      </Col>

                      <Col lg="12" className="d-flex justify-content-end">
                        {serviceArea.length > 1 && (
                          <Button
                            color="danger"
                            outline
                            onClick={() => removeService(idx)}
                          >
                            Remove
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </div>
                ))}

                <div className="d-flex justify-content-end">
                  <Button color="secondary" outline onClick={addService}>
                    + Add Service Location
                  </Button>
                </div>
              </CardBody>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col className="d-flex justify-content-center">
              <Button color="primary" type="submit" disabled={Loading}>
                {Loading ? <Spinner size="sm" /> : "Update"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default EditTransporter;
